import { Component, OnInit, ChangeDetectionStrategy, Input, Output, EventEmitter, ChangeDetectorRef, OnDestroy, inject } from '@angular/core';

import { ReactiveFormsModule, FormsModule } from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

// PrimeNG imports
import { TableModule } from 'primeng/table';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { DialogModule } from 'primeng/dialog';
import { ToastModule } from 'primeng/toast';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { TagModule } from 'primeng/tag';
import { IconFieldModule } from 'primeng/iconfield';
import { InputIconModule } from 'primeng/inputicon';

// Models and services
import { ProductResponse, ProductResponsePagedResults } from '../../models';
import { ProductService } from '../../services/product.service';
import { ProductCategoryService } from '../../services/product-category.service';
import { BrandService } from '../../services/brand.service';
import { RETAIL_LOOKUP_PAGE_SIZE } from '../../constants/retail.constants';
import { ConfirmationService } from 'primeng/api';

// Import sub-components
import { ProductFormComponent } from '../product-form/product-form.component';
import { SkuListComponent } from '../sku-list/sku-list.component';
import { CategoryListComponent } from '../category-list/category-list.component';
import { BrandListComponent } from '../brand-list/brand-list.component';
import { SupplierListComponent } from '../supplier-list/supplier-list.component';
import { Store } from '@ngrx/store';
import { COMPANY_INFO, TENANTS_INFO } from '@fitness-force/state';
import type { TENANTSINTERFACE } from '@fitness-force/models';
import { combineLatest } from 'rxjs';
import { AuthSevices, ToastMessageService } from '@fitness-force/api';
import { TooltipModule } from 'primeng/tooltip';
import { SelectModule } from 'primeng/select';

@Component({
  selector: 'lib-product-list',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    FormsModule,
    TranslateModule,
    TableModule,
    ButtonModule,
    InputTextModule,
    DialogModule,
    ToastModule,
    ConfirmDialogModule,
    TagModule,
    IconFieldModule,
    InputIconModule,
    TooltipModule,
    SelectModule,
    ProductFormComponent,
    SkuListComponent,
    CategoryListComponent,
    BrandListComponent,
    SupplierListComponent
],
  providers: [ConfirmationService],
  template: `
    <div class="card">
      <div class="flex justify-content-between align-items-center mb-3">
        <h2>{{ 'RETAIL_BILLING.PRODUCTS.TITLE' | translate }}</h2>
    
        <!-- Level Toggle - Only show if user is admin -->
        @if (isAdminUser) {
          <div class="flex gap-2">
            <p-button [label]="'RETAIL_BILLING.PRODUCTS.CURRENT_TENANT' | translate"
              [outlined]="companyLevel"
              [text]="companyLevel"
              (onClick)="selectLevel(false)"
              [class]="!companyLevel ? 'p-button-primary' : ''"
              size="small"
              aria-label="current-tenant-level-button" />
            <p-button [label]="'RETAIL_BILLING.PRODUCTS.COMPANY_LEVEL' | translate"
              [outlined]="!companyLevel"
              [text]="!companyLevel"
              (onClick)="selectLevel(true)"
              [class]="companyLevel ? 'p-button-primary' : ''"
              size="small"
              aria-label="company-level-button" />
          </div>
        }
      </div>
    
      <!-- Level Selection Info (below toggle) -->
      @if (isAdminUser) {
        <div class="flex justify-content-end mb-3">
          <div class="flex align-items-center gap-2">
            <i class="pi pi-info-circle text-primary"></i>
            <span class="text-primary font-medium text-sm">
              {{ 'RETAIL_BILLING.PRODUCTS.VIEWING' | translate }}:
              {{ companyLevel ? ('RETAIL_BILLING.PRODUCTS.COMPANY_LEVEL' | translate) : ('RETAIL_BILLING.PRODUCTS.CURRENT_TENANT' | translate) }}
            </span>
          </div>
        </div>
      }
    
      <!-- Single row with filters and action buttons -->
      <div class="flex flex-column lg:flex-row gap-3 justify-content-between align-items-start lg:align-items-center mb-4">
        <!-- Search and filters -->
        <div class="card border-round p-3 flex-1 min-w-0">
          <div class="flex flex-column md:flex-row gap-3 align-items-start md:align-items-center">
            <!-- Search Input -->
            <div class="flex-1 w-full md:w-auto min-w-16rem">
              <p-iconField iconPosition="left" class="w-full">
                <p-inputIcon styleClass="pi pi-search" />
                <input
                  pInputText
                  type="text"
                  [placeholder]="'common.PLACEHOLDER.SEARCH' | translate"
                  [(ngModel)]="searchTerm"
                  (ngModelChange)="onSearch()"
                  class="w-full"
                  aria-label="search-products-input" />
                </p-iconField>
              </div>
    
              <!-- Filter Controls -->
              <div class="flex gap-2 flex-wrap">
                <div class="w-10rem">
                  <p-select [options]="categoryOptions"
                    [(ngModel)]="selectedCategory"
                    [placeholder]="'RETAIL_BILLING.PRODUCTS.CATEGORY' | translate"
                    optionLabel="name"
                    optionValue="value"
                    [showClear]="true"
                    (onChange)="onFilter()"
                    class="w-full"
                    size="small"
                    aria-label="category-filter-dropdown" />
                </div>
    
                <div class="w-10rem">
                  <p-select [options]="brandOptions"
                    [(ngModel)]="selectedBrand"
                    [placeholder]="'RETAIL_BILLING.PRODUCTS.BRAND' | translate"
                    optionLabel="name"
                    optionValue="value"
                    [showClear]="true"
                    (onChange)="onFilter()"
                    class="w-full"
                    size="small"
                    aria-label="brand-filter-dropdown" />
                </div>
    
                <div class="w-10rem">
                  <p-select [options]="statusFilterOptions"
                    [(ngModel)]="selectedStatusFilter"
                    [placeholder]="'RETAIL_BILLING.PRODUCTS.FILTER_BY_STATUS' | translate"
                    optionLabel="label"
                    optionValue="value"
                    [showClear]="true"
                    (onChange)="onStatusFilterChange()"
                    class="w-full"
                    size="small"
                    data-testid="status-filter-dropdown" />
                </div>
    
                <!-- Reset Filters Button -->
                <p-button label="Reset"
                  icon="pi pi-refresh"
                  [outlined]="true"
                  size="small"
                  (onClick)="resetFilters()"
                  aria-label="reset-filters-button" />
              </div>
            </div>
          </div>
    
          <!-- Action Buttons -->
          <div class="flex gap-2 flex-shrink-0">
            <p-button [label]="'RETAIL_BILLING.PRODUCTS.CREATE' | translate"
              icon="pi pi-plus"
              (onClick)="showCreateDialog()"
              aria-label="create-product-button" />
            <p-button [label]="'RETAIL_BILLING.CATEGORY.MANAGE' | translate"
              icon="pi pi-list"
              [outlined]="true"
              (onClick)="showCategoriesDialog()"
              aria-label="manage-categories-button" />
            <p-button [label]="'RETAIL_BILLING.BRAND.MANAGE' | translate"
              icon="pi pi-bookmark"
              [outlined]="true"
              (onClick)="showBrandsDialog()"
              aria-label="manage-brands-button" />
            <p-button [label]="'RETAIL_BILLING.SUPPLIER.MANAGE' | translate"
              icon="pi pi-truck"
              [outlined]="true"
              (onClick)="showSuppliersDialog()"
              aria-label="manage-suppliers-button" />
          </div>
        </div>
    
        <!-- Products table -->
        <p-table
          [value]="products"
          [loading]="loading"
          [paginator]="true"
          [lazy]="true"
          [rows]="pageSize"
          [(first)]="first"
          [totalRecords]="totalRecords"
          (onPage)="goToNextPage($event)"
          [showCurrentPageReport]="true"
          currentPageReportTemplate="Showing {first} to {last} of {totalRecords} entries"
          [rowsPerPageOptions]="[10, 25, 50]"
          styleClass="p-datatable-gridlines">
    
          <ng-template pTemplate="header">
            <tr>
              <th style="width: 35%">{{ 'RETAIL_BILLING.PRODUCTS.NAME' | translate }}</th>
              <th style="width: 10%">{{ 'RETAIL_BILLING.PRODUCTS.CODE' | translate }}</th>
              <th style="width: 20%">{{ 'RETAIL_BILLING.PRODUCTS.CATEGORY' | translate }}</th>
              <th style="width: 15%">{{ 'RETAIL_BILLING.PRODUCTS.BRAND' | translate }}</th>
              <th style="width: 5%">{{ 'RETAIL_BILLING.PRODUCTS.IS_QUICK_SALE' | translate }}</th>
              <th style="width: 5%">{{ 'RETAIL_BILLING.PRODUCTS.IS_POS_FAVOURITE' | translate }}</th>
              <th style="width: 10%">{{ 'RETAIL_BILLING.PRODUCTS.STATUS' | translate }}</th>
              <th style="width: 10%">{{ 'RETAIL_BILLING.COMMON.ACTIONS' | translate }}</th>
            </tr>
          </ng-template>
    
          <ng-template pTemplate="body" let-product>
            <tr>
              <td class="p-3">
                <div class="flex align-items-center gap-3">
                  @if (product.image_url) {
                    <img
                      [src]="product.image_url"
                      [alt]="product.name_primary_language"
                      class="w-3rem h-3rem border-round object-cover" />
                  }
                  <div class="flex-1 min-w-0">
                    <div class="font-medium text-900 line-height-1 mb-1">{{ product.name_primary_language }}</div>
                    <div class="text-sm text-500 line-height-1">{{ product.name_secondary_language }}</div>
                  </div>
                </div>
              </td>
              <td class="p-3">
                <span class="text-900">{{ product?.code }}</span>
              </td>
              <td class="p-3">
                <span class="text-900">{{ product?.product_category_name_primary || product?.product_category_name_secondary }}</span>
              </td>
              <td class="p-3">
                <span class="text-900">{{ product?.brand_name_primary || product?.brand_name_secondary }}</span>
              </td>
              <td class="p-3 text-center">
                @if (product.is_quick_sale) {
                  <i class="pi pi-bolt text-orange-500" [pTooltip]="'RETAIL_BILLING.PRODUCTS.IS_QUICK_SALE' | translate" tooltipPosition="top" aria-label="quick-sale-indicator"></i>
                }
              </td>
              <td class="p-3 text-center">
                @if (product.is_pos_favourite) {
                  <i class="pi pi-star-fill text-yellow-500" [pTooltip]="'RETAIL_BILLING.PRODUCTS.IS_POS_FAVOURITE' | translate" tooltipPosition="top" aria-label="pos-favourite-indicator"></i>
                }
              </td>
              <td class="p-3">
                <div class="flex align-items-center gap-2">
                  <p-tag [value]="product.is_active ? ('RETAIL_BILLING.PRODUCTS.ACTIVE' | translate) : ('RETAIL_BILLING.PRODUCTS.INACTIVE' | translate)"
                    [severity]="product.is_active ? 'success' : 'danger'" />
                  @if (product.product_category_is_active === false) {
                    <i
                      class="pi pi-exclamation-triangle text-orange-500 cursor-pointer"
                      [pTooltip]="'RETAIL_BILLING.PRODUCTS.INACTIVE_CATEGORY_POS_WARNING' | translate"
                      tooltipPosition="top"
                      aria-label="inactive-category-pos-warning"></i>
                  }
                </div>
              </td>
              <td class="p-3">
                <div class="flex gap-1 justify-content-center">
                  <p-button icon="pi pi-pencil"
                    tooltipPosition="top"
                    tooltip="Edit Product"
                    [text]="true"
                    [rounded]="true"
                    [outlined]="true"
                    size="small"
                    (onClick)="editProduct(product)"
                    [ariaLabel]="'edit-product-' + product.id" />
                  <p-button icon="pi pi-list"
                    tooltipPosition="top"
                    tooltip="Manage SKUs"
                    [text]="true"
                    [rounded]="true"
                    [outlined]="true"
                    size="small"
                    (onClick)="manageSkus(product)"
                    [ariaLabel]="'manage-skus-' + product.id" />
                  @if (false) {
                    <p-button icon="pi pi-trash"
                      [text]="true"
                      [rounded]="true"
                      [outlined]="true"
                      size="small"
                      severity="danger"
                      (onClick)="deleteProduct(product)"
                      [ariaLabel]="'delete-product-' + product.id" />
                  }
                </div>
              </td>
            </tr>
          </ng-template>
    
          <ng-template pTemplate="emptymessage">
            <tr>
              <td colspan="8" class="text-center p-4">{{ 'RETAIL_BILLING.PRODUCTS.NO_PRODUCTS' | translate }}</td>
            </tr>
          </ng-template>
        </p-table>
      </div>
    
      @if (showProductDialog) {
        <lib-product-form [visible]="showProductDialog"
          [product]="selectedProduct"
          [tenantId]="tenantId"
          [companyLevel]="companyLevel"
          [primaryLanguage]="primaryLanguage"
          [secondaryLanguage]="secondaryLanguage"
          [isSecondaryLanguage]="isSecondaryLanguage"
          (onSave)="onProductSaved($event)"
          (onCancel)="hideProductDialog()" />
      }
    
      @if (showSkuDialog) {
        <lib-sku-list [visible]="showSkuDialog"
          [product]="selectedProduct"
          [tenantId]="tenantId"
          [companyLevel]="companyLevel"
          [primaryLanguage]="primaryLanguage"
          [secondaryLanguage]="secondaryLanguage"
          [isSecondaryLanguage]="isSecondaryLanguage"
          (onClose)="hideSkuDialog()" />
      }
    
      @if (showCategoriesDialogFlag) {
        <lib-category-list [visible]="showCategoriesDialogFlag"
          [companyLevel]="companyLevel"
          [tenantId]="tenantId"
          [primaryLanguage]="primaryLanguage"
          [secondaryLanguage]="secondaryLanguage"
          [isSecondaryLanguage]="isSecondaryLanguage"
          (onClose)="hideCategoriesDialog()" />
      }
    
      @if (showBrandsDialogFlag) {
        <lib-brand-list [visible]="showBrandsDialogFlag"
          [companyLevel]="companyLevel"
          [tenantId]="tenantId"
          [primaryLanguage]="primaryLanguage"
          [secondaryLanguage]="secondaryLanguage"
          [isSecondaryLanguage]="isSecondaryLanguage"
          (onClose)="hideBrandsDialog()" />
      }

      @if (showSuppliersDialogFlag) {
        <lib-supplier-list [visible]="showSuppliersDialogFlag"
          [companyLevel]="companyLevel"
          [tenantId]="tenantId"
          [primaryLanguage]="primaryLanguage"
          [secondaryLanguage]="secondaryLanguage"
          [isSecondaryLanguage]="isSecondaryLanguage"
          (onClose)="hideSuppliersDialog()" />
      }
    
    
      <p-confirmDialog />
      <p-toast />
    `,
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ProductListComponent implements OnInit, OnDestroy {
  private readonly productService = inject(ProductService);
  private readonly categoryService = inject(ProductCategoryService);
  private readonly brandService = inject(BrandService);
  private readonly confirmationService = inject(ConfirmationService);
  private readonly translate = inject(TranslateService);
  private readonly store = inject(Store);
  private readonly changeDetectorRef = inject(ChangeDetectorRef);
  private readonly toastService = inject(ToastMessageService);
  private readonly authService = inject(AuthSevices);


  tenantId?: number;
  companyLevel: boolean = true;
  isSecondaryLanguage: boolean = false;
  secondaryLanguage: string = '';
  primaryLanguage: string = '';
  isAdminUser: boolean = false;

  products: ProductResponse[] = [];
  loading = false;
  totalRecords = 0;
  pageSize = 10;
  pageNumber = 1;
  first = 0;
  
  searchTerm = '';
  selectedCategory: number | null = null;
  selectedBrand: number | null = null;
  selectedStatusFilter: boolean | null = null;
  
  categoryOptions: any[] = [];
  brandOptions: any[] = [];
  statusFilterOptions = [
    { label: '', value: true },
    { label: '', value: false }
  ];
  
  showProductDialog = false;
  showSkuDialog = false;
  showCategoriesDialogFlag = false;
  showBrandsDialogFlag = false;
  showSuppliersDialogFlag = false;
  selectedProduct: ProductResponse | null = null;

  requestParams = new Map();
  private readonly destroy$ = new Subject<void>();

  ngOnInit() {
    this.requestParams.set('PageNumber', { name: 'PageNumber', value: this.pageNumber });
    this.requestParams.set('PageSize', { name: 'PageSize', value: this.pageSize });
    this.componentInit();
  }

  componentInit() {
      if(this.authService.currentUserDetails().role.toLowerCase() === 'admin' || this.authService.currentUserDetails().role.toLowerCase() === 'ffadmin' || this.authService.currentUserDetails().role.toLowerCase() === 'ff_admin') {
        this.isAdminUser = true;
      }

      combineLatest([
        this.store.select(TENANTS_INFO),
        this.store.select(COMPANY_INFO),
      ])
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: ([tenant, company]: any) => {
          this.tenantId = tenant.selected_tenant.id;
          if (tenant.selected_tenant?.secondary_language) {
            this.isSecondaryLanguage = true;
            this.translate
              .get([
                'common.LANGUAGE.' +
                tenant.selected_tenant.primary_language?.toUpperCase(),
                'common.LANGUAGE.' +
                tenant.selected_tenant.secondary_language?.toUpperCase(),
              ])
              .pipe(takeUntil(this.destroy$))
              .subscribe((translations) => {
                this.primaryLanguage =
                  translations[
                  'common.LANGUAGE.' +
                  tenant.selected_tenant.primary_language?.toUpperCase()
                  ];
                this.secondaryLanguage =
                  translations[
                  'common.LANGUAGE.' +
                  tenant.selected_tenant.secondary_language?.toUpperCase()
                  ];
              });
          }
          if(tenant.selected_tenant.id > 0) {
            this.store.dispatch({ type: 'ROUTER_GO' });
            this.loadData();
          }
        }
      });
  }

  loadData() {
    this.initializeStatusFilterOptions();
    
    // Load categories
    this.categoryService.getCompanyCategories({ page: 1, page_size: RETAIL_LOOKUP_PAGE_SIZE })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response: any) => {
          this.categoryOptions = response.items?.map((cat: any) => ({
            name: cat.name_primary_language,
            value: cat.id
          })) || [];
          this.changeDetectorRef.detectChanges();
        },
        error: (error: any) => {
          this.toastService.showError(error);
        }
      });

    // Load brands
    this.brandService.getCompanyBrands({ page: 1, page_size: RETAIL_LOOKUP_PAGE_SIZE })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response: any) => {
          this.brandOptions = response.items?.map((brand: any) => ({
            name: brand.name_primary_language,
            value: brand.id
          })) || [];
          this.changeDetectorRef.detectChanges();
        },
        error: (error: any) => {
          this.toastService.showError(error);
        }
      });
    this.loadProducts();
  }

  initializeStatusFilterOptions() {
    this.statusFilterOptions = [
      { 
        label: this.translate.instant('RETAIL_BILLING.PRODUCTS.ACTIVE'), 
        value: true 
      },
      { 
        label: this.translate.instant('RETAIL_BILLING.PRODUCTS.INACTIVE'), 
        value: false 
      }
    ];
  }

  loadProducts() {
    this.loading = true;

    // Clear previous search/filter params
    this.requestParams.delete('ProductName');
    this.requestParams.delete('CategoryId');
    this.requestParams.delete('BrandId');
    this.requestParams.delete('IsActive');

    // Add search term if exists
    if(this.searchTerm) {
      this.requestParams.set('ProductName', { name: 'ProductName', value: this.searchTerm });
    }

    // Add category filter if selected
    if(this.selectedCategory) {
      this.requestParams.set('CategoryId', { name: 'CategoryId', value: this.selectedCategory });
    }

    // Add brand filter if selected
    if(this.selectedBrand) {
      this.requestParams.set('BrandId', { name: 'BrandId', value: this.selectedBrand });
    }

    // Add status filter if selected
    if(this.selectedStatusFilter !== null) {
      this.requestParams.set('IsActive', { name: 'IsActive', value: this.selectedStatusFilter });
    }

    const loadFn = this.companyLevel 
      ? this.productService.getCompanyProducts(this.requestParams)
      : this.productService.getTenantProducts(this.tenantId!, this.requestParams);

    loadFn
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response: ProductResponsePagedResults) => {
          this.products = response.items || [];
          this.totalRecords = response.total_items;
          if (response.page_size) {
            this.pageSize = response.page_size;
          }
          if (response.current_page) {
            this.pageNumber = response.current_page;
            this.first = (response.current_page - 1) * this.pageSize;
          }
          this.loading = false;
          this.changeDetectorRef.detectChanges();
        },
        error: (error: any) => {
          console.error('Error loading products:', error);
          this.toastService.showError(error);
          this.loading = false;
          this.changeDetectorRef.detectChanges();
        }
      });
  }

  private resetPagination(): void {
    this.pageNumber = 1;
    this.first = 0;
    this.requestParams.set('PageNumber', { name: 'PageNumber', value: this.pageNumber });
    this.requestParams.set('PageSize', { name: 'PageSize', value: this.pageSize });
  }

  onSearch() {
    this.resetPagination();
    this.loadProducts();
  }

  onFilter() {
    this.resetPagination();
    this.loadProducts();
  }

  onStatusFilterChange() {
    this.resetPagination();
    this.loadProducts();
  }

  resetFilters() {
    this.searchTerm = '';
    this.selectedCategory = null;
    this.selectedBrand = null;
    this.selectedStatusFilter = null;
    this.resetPagination();
    this.loadProducts();
  }

  showCreateDialog() {
    this.selectedProduct = null;
    this.showProductDialog = true;
  }

  editProduct(product: ProductResponse) {
    this.selectedProduct = product;
    this.showProductDialog = true;
  }

  manageSkus(product: ProductResponse) {
    this.selectedProduct = product;
    this.showSkuDialog = true;
  }

  deleteProduct(product: ProductResponse) {
    this.confirmationService.confirm({
      message: this.translate.instant('RETAIL_BILLING.COMMON.CONFIRM_DELETE'),
      header: this.translate.instant('RETAIL_BILLING.PRODUCTS.DELETE'),
      icon: 'pi pi-exclamation-triangle',
      acceptButtonStyleClass: 'p-button-danger',
      accept: () => {
        this.loadProducts();
      }
    });
  }

  onProductSaved(product: ProductResponse) {
    this.hideProductDialog();
    this.loadProducts();
    const message = this.selectedProduct 
      ? 'RETAIL_BILLING.PRODUCTS.UPDATE_SUCCESS'
      : 'RETAIL_BILLING.PRODUCTS.CREATE_SUCCESS';
    this.toastService.showSuccess(this.translate.instant(message));
  }

  hideProductDialog() {
    this.showProductDialog = false;
    this.selectedProduct = null;
  }

  hideSkuDialog() {
    this.showSkuDialog = false;
    this.selectedProduct = null;
    this.changeDetectorRef.detectChanges();
  }

  showCategoriesDialog() {
    this.showCategoriesDialogFlag = true;
  }

  hideCategoriesDialog() {
    this.showCategoriesDialogFlag = false;
    this.changeDetectorRef.detectChanges();
  }

  showBrandsDialog() {
    this.showBrandsDialogFlag = true;
  }

  hideBrandsDialog() {
    this.showBrandsDialogFlag = false;
    this.changeDetectorRef.detectChanges();
  }

  showSuppliersDialog() {
    this.showSuppliersDialogFlag = true;
  }

  hideSuppliersDialog() {
    this.showSuppliersDialogFlag = false;
    this.changeDetectorRef.detectChanges();
  }

  selectLevel(isCompanyLevel: boolean) {
    this.companyLevel = isCompanyLevel;
    this.resetPagination();
    this.loadProducts();
  }

  goToNextPage(e: any) {
    this.pageSize = e.rows;
    this.first = e.first;
    this.pageNumber = e.first ? e.first / this.pageSize + 1 : 1;
    this.requestParams.delete('PageNumber');
    this.requestParams.delete('PageSize');
    this.requestParams.set('PageNumber', { name: 'PageNumber', value: this.pageNumber });
    this.requestParams.set('PageSize', { name: 'PageSize', value: this.pageSize });
    this.loadProducts();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.showProductDialog = false;
    this.showSkuDialog = false;
    this.showCategoriesDialogFlag = false;
    this.showBrandsDialogFlag = false;
    this.showSuppliersDialogFlag = false;
    this.selectedProduct = null;
    this.selectedCategory = null;
    this.selectedBrand = null;
    this.selectedStatusFilter = null;
  }

}